/**
 * Joins an array of strings or numbers into a single string using a separator.
 *
 * @param {(string | number | null | undefined)[]} strings - The values to join.
 * @param {string} separator - The string used to separate each value.
 * @returns {string} The joined string.
 */
export function joinToString(strings, separator) {
    return strings
        .filter((s) => s != null)
        .map((s) => (typeof s === 'number' ? s.toString() : s === null || s === void 0 ? void 0 : s.trim()))
        .filter((s) => s)
        .join(separator);
}
/**
 * Joins multiple labels with a " - " separator.
 *
 * @param {...(string | number | null | undefined)[]} labels - The label values to join.
 * @returns {string} The joined label string.
 */
export function joinLabels(...labels) {
    return joinToString(labels, ' - ');
}
/**
 * Joins values with a ", " separator.
 *
 * @param {(string | number | null | undefined)[]} values - The values to join.
 * @returns {string} The joined string.
 */
export function joinValues(values) {
    return joinToString(values, ', ');
}
/**
 * Extracts label-value pairs from child widget props.
 *
 * @param {WidgetProps<any, any>} props - The parent widget props.
 * @param {string} [labelPrefix] - Optional label prefix.
 * @returns {{ label: string; text: string }[]} Array of label-text pairs.
 */
export function getLabelAndValuePairsFromChildProps(props, labelPrefix) {
    if (props.childProps) {
        return props.childProps.flatMap((childProp) => {
            if (childProp.widget.getLabelValueAsTextPairs) {
                return childProp.widget.getLabelValueAsTextPairs(childProp, labelPrefix);
            }
            else {
                return [];
            }
        });
    }
    else {
        return [];
    }
}
/**
 * Extracts label-value pairs from dynamic rows in a widget.
 *
 * @param {WidgetProps<any, any>} props - The widget props containing dynamic rows.
 * @param {string} [labelPrefix] - Optional prefix for labels.
 * @returns {{ label: string; text: string }[]} Array of label-text pairs.
 */
export function getLabelAndValuePairsFromChildDynamicRows(props, labelPrefix) {
    if (props.childDynamicRows) {
        return props.childDynamicRows.flatMap((row, rowIndex) => {
            return row.childProps.flatMap((childProp) => {
                if (childProp.widget.getLabelValueAsTextPairs) {
                    return childProp.widget.getLabelValueAsTextPairs(childProp, joinLabels(labelPrefix, rowIndex + 1));
                }
                else {
                    return [];
                }
            });
        });
    }
    else {
        return [];
    }
}
/**
 * Constructs a label-text pair if value is defined and not empty.
 *
 * @param {string} label - The label text.
 * @param {string | number | null | undefined} value - The value to associate.
 * @returns {{ label: string; text: string } | undefined} A single label-text pair or undefined.
 */
export function getLabelAndValuePair(label, value) {
    if (value != null) {
        const text = typeof value === 'number' ? value.toString() : value.trim();
        if (text) {
            return { label, text: text };
        }
    }
    return undefined;
}
/**
 * Constructs a label-text pair if value is defined and not empty.
 *
 * @param {string} label - The label text.
 * @param {string | number | null | undefined} text - The value to associate.
 * @returns {{ label: string; text: string } | undefined} A single label-text pair or undefined.
 */
export function getLabelAndValuePairs(label, text) {
    const pair = getLabelAndValuePair(label, text);
    if (pair) {
        return [pair];
    }
    else {
        return [];
    }
}
